/**
 * QuestionsLibrary — Questions tab + Groups tab.
 * Faithful port of screens-questions.jsx.
 */
import { useState, useEffect, type ReactNode , type CSSProperties} from "react";
import { alpha, Btn, Card, Dropdown, Icon, IconBtn, Input, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { FilterBar, EmptyState, ExplainerFooter, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CARD_GRID, CardActionBar } from "../../shell/list-composites";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { useQuestions, QFIELD_TYPES, QTYPE, type Question, type QuestionGroup } from "./useQuestions";
import { useFolderLibrary, FolderShellFrame, FolderShellBars, FolderShellOverlays } from "../shared/library-shell";
import { useT, t as i18nT } from "../../lib/i18n";

/* ── Shared glyphs ───────────────────────────────────────────────────────── */
export function QGlyph({ type, size = 15, color }: { type: string; size?: number; color?: string }) {
  const t = QTYPE(type); const s = size;
  const st: CSSProperties = { width: s, height: s, flexShrink: 0 };
  const P = (d: ReactNode) => <svg viewBox="0 0 20 20" fill="none" stroke={color ?? "currentColor"} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={st}>{d}</svg>;
  switch (t.icon) {
    case "heading":  return P(<><path d="M4 4v12M12 4v12M4 10h8"/><path d="M15 7v9"/></>);
    case "text":     return P(<><path d="M4 5h12M4 9h12M4 13h7"/></>);
    case "input":    return P(<><rect x="3" y="7" width="14" height="6" rx="1.5"/><path d="M6 10h0.01"/></>);
    case "area":     return P(<><rect x="3" y="4" width="14" height="12" rx="1.5"/><path d="M6 8h8M6 11h8M6 14h4"/></>);
    case "hash":     return P(<><path d="M7 4 6 16M14 4l-1 12M4 8h12M3.5 12h12"/></>);
    case "radio":    return P(<><circle cx="10" cy="10" r="6.5"/><circle cx="10" cy="10" r="2.5" fill={color ?? "currentColor"}/></>);
    case "caret":    return P(<><rect x="3" y="6" width="14" height="8" rx="1.5"/><path d="m9 9 1.5 1.5L12 9"/></>);
    case "toggle":   return P(<><rect x="2" y="6" width="16" height="8" rx="4"/><circle cx="13" cy="10" r="2.5" fill={color ?? "currentColor"}/></>);
    case "checks":   return P(<><rect x="3" y="3" width="6" height="6" rx="1.4"/><path d="m4.5 6 1 1 2-2"/><rect x="3" y="11" width="6" height="6" rx="1.4"/><path d="M12 6h5M12 14h5"/></>);
    case "multi":    return P(<><rect x="3" y="5" width="14" height="10" rx="1.5"/><path d="m12 8 1.4 1.4L15 8"/><path d="M6 8.5h4M6 11.5h5"/></>);
    case "calendar": return P(<><rect x="3" y="4" width="14" height="13" rx="2"/><path d="M3 8h14M7 2v4M13 2v4M10 12v2"/></>);
    case "upload":   return P(<><path d="M10 13V5M7 8l3-3 3 3"/><path d="M4 14v1.5A1.5 1.5 0 0 0 5.5 17h9a1.5 1.5 0 0 0 1.5-1.5V14"/></>);
    default:         return P(<><path d="M4 5h12M4 9h12M4 13h7"/></>);
  }
}

function QTypeBadge({ type }: { type: string }) {
  const t = QTYPE(type);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 9px 3px 7px", borderRadius: 999, background: T.n6, border: `1px solid ${T.border}`, fontSize: 12, fontWeight: 500, color: T.n2, whiteSpace: "nowrap" }}>
      <QGlyph type={type} size={13} color={T.p1}/> {t.label}
    </span>
  );
}

const qTh: CSSProperties = { padding: "10px 16px", textAlign: "left", fontWeight: 600, fontSize: 11, textTransform: "uppercase", color: T.soft };
const qTd: CSSProperties = { padding: "12px 16px", verticalAlign: "middle" };

function questionsTabs(nav: ReturnType<typeof useNav>, qs: Question[], gs: QuestionGroup[]) {
  return [
    { id: "questions", label: i18nT("questions.library.bookingQuestions", "Booking Questions"), count: qs.length, onClick: () => nav.go("questions") },
    { id: "groups",    label: i18nT("questions.library.bookingQuestionFlows", "Booking Question flows"),    count: gs.length, onClick: () => nav.go("questions.groups") },
  ];
}

/* ── Questions tab ───────────────────────────────────────────────────────── */
function QuestionsTab() {
  const t = useT();
  const nav = useNav();
  const store = useQuestions();
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [view, setView] = useViewMode("questions:view");
  const canWrite = can("questions.write");
  const all = store.questions();
  const gs = store.groups();
  /* Folder organization — the shared Folder Library shell (family "questions"):
     mounted, not copied, so the folders UX is identical across libraries. */
  const lib = useFolderLibrary({
    family: "questions",
    ids: all.map((x) => x.id),
    canWrite,
    allLabel: t("questions.library.allQuestions", "All questions"),
    searchAllLabel: t("collections.searchEverywhereQuestions", "Search all questions"),
    query,
    failTitle: t("questions.library.couldnTUpdateQuestion", "Couldn't update question"),
    onAfterPurge: () => store.retryLoad(),
  });
  const poolSet = new Set(lib.pool);
  const filtered = all.filter((q) => {
    if (!poolSet.has(q.id)) return false;
    if (typeFilter && q.type !== typeFilter) return false;
    if (query.trim() && !q.label.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const dup = async (q: Question) => {
    try {
      const c = await store.duplicateQ(q.id);
      toast({ tone: "success", title: t("questions.library.questionDuplicated", "Question duplicated"), subtitle: i18nT("questions.library.cLabelAdded.icu", { value0: c.label }) });
    } catch (e) {
      console.warn("duplicateQuestion failed:", e);
      toast({ tone: "danger", title: t("questions.library.couldnTDuplicateThatQuestion", "Couldn't duplicate that question"), subtitle: t("questions.library.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment.") });
    }
  };
  const del = async (q: Question) => {
    const used = store.usageOf(q.id);
    if (used > 0) { toast({ tone: "warn", title: t("questions.library.canTDeleteInUse", "Can't delete — in use"), subtitle: t("questions.library.removeFromUsedGroupUsed1SFirst.icu", { count: used }) }); return; }
    try {
      await store.removeQ(q.id);
      toast({ tone: "warn", title: t("questions.library.questionDeleted", "Question deleted") });
    } catch (e) {
      console.warn("deleteQuestion failed:", e);
      toast({ tone: "danger", title: t("questions.library.couldnTDeleteThatQuestion", "Couldn't delete that question"), subtitle: t("questions.library.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment.") });
    }
  };
  const mkActions = (q: Question) => ({
    onView: () => nav.go("questions.edit", { id: q.id, view: 1 }), viewLabel: "Preview",
    onEdit: canWrite ? () => nav.go("questions.edit", { id: q.id }) : undefined,
    onDuplicate: canWrite ? () => dup(q) : undefined,
    moreHeader: q.label || "Question",
    moreItems: canWrite ? [
      lib.foldersMenuItem(q.id),
      { icon: <Icon.trash size={14}/>, label: t("questions.library.deleteQuestion", "Delete question"), danger: true as const, onClick: () => del(q) },
    ] : [],
  });
  const openQ = (q: Question) => nav.go("questions.edit", canWrite ? { id: q.id } : { id: q.id, view: 1 });

  return (
    <Screen rail="cs-questions" tabs={questionsTabs(nav, all, gs)} activeTab="questions">
      <PageHeader
        title={t("questions.library.bookingQuestionsc393fab8", "Booking questions")}
        subtitle={t("questions.library.adminAuthoredQuestionsForEventsGroupThemBranchThemThenApplyTo", "Admin-authored questions for events. Group them, branch them, then apply to events.")}
        actions={<>
          <ViewToggle mode={view} onChange={setView}/>
          {canWrite ? <Btn icon={<Icon.plus/>} onClick={() => nav.go("questions.edit", { new: 1 })}>{t("questions.library.newQuestion", "New question")}</Btn> : null}
        </>}
      />
      <FolderShellFrame lib={lib}>
        {store.loadError() ? (
          <div style={{ marginBottom: 12, padding: "10px 14px", background: alpha(T.red1, "12"), border: `1px solid ${alpha(T.red1, "33")}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ color: T.red1, display: "inline-flex" }}><Icon.warn size={14} /></span>
            <span style={{ fontSize: 12.5, color: T.red1, fontWeight: 600, flex: 1 }}>{t("questions.library.couldnTLoadTheLibrary", "Couldn't load the question library")} — {store.loadError()}</span>
            <Btn variant="secondary" size="sm" onClick={() => store.retryLoad()}>{t("common.actions.retry", "Retry")}</Btn>
          </div>
        ) : null}
        <div style={{ padding: "10px 0 4px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Input full={false} leading={<Icon.search size={14} />} placeholder={t("questions.library.findAQuestion", "Find a question…")} value={query} onChange={(e) => setQuery(e.target.value)} style={{ minWidth: 240 }}/>
          <Dropdown
            options={[{ value: "all", label: t("questions.library.allTypes", "All types") }, ...QFIELD_TYPES.filter((q) => q.value).map((q) => ({ value: q.id, label: q.label }))]}
            value={typeFilter || "all"}
            onChange={(v) => setTypeFilter(v === "all" ? "" : v)}
            aria-label={t("questions.library.allTypes", "All types")}
            testid="questions-type-filter"
            style={{ gap: 0, width: 180, flexShrink: 0 }}
          />
          <span style={{ flex: 1 }}/>
          <span style={{ fontSize: 12, color: T.soft }}>{filtered.length} {t("questions.library.of", "of")}{" "}{lib.pool.length}</span>
        </div>
        <FolderShellBars lib={lib}/>
        <ExplainerFooter><b>{t("questions.library.sharedAdminAuthored", "Shared & admin-authored.")}</b> {t("questions.library.editAQuestionHereAndEveryGroupAndEventThatUsesIt", "Edit a question here and every group and event that uses it updates automatically.")}</ExplainerFooter>

        {filtered.length === 0
          ? <EmptyState variant="card" icon={<Icon.help size={26}/>} title={query.trim() ? t("questions.library.noQuestionsMatch", "No questions match") : lib.scope !== "all" ? t("questions.library.emptyFolderTitle", "Nothing in this folder yet") : t("questions.library.noQuestionsMatch", "No questions match")}
              body={query.trim() ? t("questions.library.clearFiltersOrCreateANewQuestion", "Clear filters or create a new question.") : lib.scope !== "all" ? t("questions.library.emptyFolderBody", "Drag questions here, or use a question's Folders… action.") : t("questions.library.clearFiltersOrCreateANewQuestion", "Clear filters or create a new question.")}
              action={canWrite ? <Btn icon={<Icon.plus size={14}/>} onClick={() => nav.go("questions.edit", { new: 1 })}>{t("questions.library.newQuestion", "New question")}</Btn> : null}/>
          : view === "grid" ? (
            <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16, marginTop: 14 }}>
              {filtered.map((q) => {
                const used = store.usageOf(q.id);
                const sub = q.options?.length ? q.options.map((o) => o.label).join(" · ") : (q.help || "");
                return (
                  <Card key={q.id} {...lib.dragProps(q.id)} style={{ padding: 0, display: "flex", flexDirection: "column", cursor: "pointer", overflow: "hidden", outline: lib.sel.has(q.id) ? `2px solid ${T.p1}` : "none" }} onClick={() => openQ(q)}>
                    <div style={{ padding: "16px 18px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 11 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        {canWrite ? <input type="checkbox" checked={lib.sel.has(q.id)} onClick={(e) => e.stopPropagation()} onChange={(e) => lib.toggleSel(q.id, e.target.checked)} style={{ accentColor: T.p1, marginTop: 8 }} /> : null}
                        <span style={{ width: 32, height: 32, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><QGlyph type={q.type} size={16}/></span>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 14.5, fontWeight: 700, color: T.n1 }}>{q.label || "Untitled question"}</div>
                          {sub ? <div style={{ fontSize: 12, color: T.soft, marginTop: 3, lineHeight: 1.4, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" } as any}>{sub}</div> : null}
                        </div>
                      </div>
                      <QTypeBadge type={q.type}/>
                      <div style={{ marginTop: "auto", paddingTop: 10, borderTop: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 14, fontSize: 12, color: T.soft }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon.layers size={12}/> {used ? /* TODO(i18n): replace template-literal with t('questions.library.text', { ... }) */`${used} group${used === 1 ? "" : "s"}` : t('questions.library.text2')}</span>
                        {lib.folderChips(q.id)}
                        <span style={{ marginLeft: "auto" }}>{q.updated}</span>
                      </div>
                    </div>
                    <CardActionBar {...mkActions(q)}/>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card style={{ padding: 0, marginTop: 14 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
                <thead><tr style={{ background: T.n6 }}>
                  {canWrite ? <th style={{ ...qTh, width: 34, paddingRight: 0 }}><input type="checkbox" aria-label="Select all" checked={filtered.length > 0 && filtered.every((r) => lib.sel.has(r.id))} onChange={(e) => lib.setSel(e.target.checked ? new Set(filtered.map((r) => r.id)) : new Set())} style={{ accentColor: T.p1 }} /></th> : null}
                  <th style={qTh}>{t("questions.library.question", "Question")}</th><th style={qTh}>{t("questions.library.type", "Type")}</th><th style={qTh}>{t("questions.library.inGroups", "In groups")}</th><th style={qTh}>{t("questions.library.updated", "Updated")}</th><th style={{ ...qTh, width: 140 }}>{t("questions.library.actions", "Actions")}</th></tr></thead>
                <tbody>
                  {filtered.map((q, i) => {
                    const used = store.usageOf(q.id);
                    return (
                      <tr key={q.id} {...lib.dragProps(q.id)} onClick={() => openQ(q)} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", background: lib.sel.has(q.id) ? T.p4 : "transparent" }}>
                        {canWrite ? <td style={{ ...qTd, width: 34, paddingRight: 0 }} onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={lib.sel.has(q.id)} onChange={(e) => lib.toggleSel(q.id, e.target.checked)} style={{ accentColor: T.p1 }} /></td> : null}
                        <td style={qTd}><div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ width: 30, height: 30, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><QGlyph type={q.type} size={15}/></span>
                          <div><div style={{ color: T.n1, fontWeight: 600, display: "flex", alignItems: "center" }}>{q.label || "Untitled"}{lib.folderChips(q.id)}</div>{q.help ? <div style={{ color: T.soft, fontSize: 11.5 }}>{q.help}</div> : null}</div>
                        </div></td>
                        <td style={qTd}><QTypeBadge type={q.type}/></td>
                        <td style={{ ...qTd, color: used ? T.n1 : T.soft }}>{used || "—"}</td>
                        <td style={{ ...qTd, color: T.soft }}>{q.updated}</td>
                        <td style={qTd} onClick={(e) => e.stopPropagation()}><RowActions {...mkActions(q)}/></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
      </FolderShellFrame>
      <FolderShellOverlays lib={lib}/>
    </Screen>
  );
}

/* ── Groups tab ─────────────────────────────────────────────────────────── */
function GroupsTab() {
  const t = useT();
  const nav = useNav();
  const store = useQuestions();
  const [query, setQuery] = useState("");
  const [view, setView] = useViewMode("groups:view");
  const canWrite = can("questions.write");
  const all = store.questions();
  const allGroups = store.groups();
  const groups = allGroups.filter((g) => !query.trim() || g.name.toLowerCase().includes(query.toLowerCase()));
  const branchCount = (g: QuestionGroup) => g.items.reduce((n, it) => n + (it.rules ? Object.keys(it.rules).length : 0), 0);
  const dup = async (g: QuestionGroup) => {
    try {
      const c = await store.duplicateGroup(g.id);
      toast({ tone: "success", title: t("questions.library.groupDuplicated", "Group duplicated"), subtitle: i18nT("questions.library.cNameAdded.icu", { value0: c.name }) });
    } catch (e) {
      console.warn("duplicateQuestionGroup failed:", e);
      toast({ tone: "danger", title: t("questions.library.couldnTDuplicateThatGroup", "Couldn't duplicate that group"), subtitle: t("questions.library.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment.") });
    }
  };
  const del = async (g: QuestionGroup) => {
    try {
      await store.removeGroup(g.id);
      toast({ tone: "warn", title: t("questions.library.groupDeleted", "Group deleted") });
    } catch (e) {
      console.warn("deleteQuestionGroup failed:", e);
      toast({ tone: "danger", title: t("questions.library.couldnTDeleteThatGroup", "Couldn't delete that group"), subtitle: t("questions.library.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment.") });
    }
  };
  const mkActions = (g: QuestionGroup) => ({
    onView: () => nav.go("questions.group.edit", { id: g.id, view: 1 }), viewLabel: "Preview",
    onEdit: canWrite ? () => nav.go("questions.group.edit", { id: g.id }) : undefined,
    onDuplicate: canWrite ? () => dup(g) : undefined,
    moreHeader: g.name || "Group",
    moreItems: canWrite ? [{ icon: <Icon.trash size={14}/>, label: t("questions.library.deleteGroup", "Delete group"), danger: true as const, onClick: () => del(g) }] : [],
  });

  return (
    <Screen rail="cs-questions" tabs={questionsTabs(nav, all, allGroups)} activeTab="groups">
      <PageHeader title={t("questions.library.bookingQuestionFlows", "Booking Question flows")} subtitle={t("questions.library.orderQuestionsAndSetBranchingRules", "Order questions and set branching rules.")}
        actions={<>
          <ViewToggle mode={view} onChange={setView}/>
          {canWrite ? <Btn icon={<Icon.plus/>} onClick={() => nav.go("questions.group.edit", { new: 1 })}>{t("questions.library.newGroup", "New group")}</Btn> : null}
        </>}/>
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 28px", display: "flex", flexDirection: "column" }}>
        <div style={{ margin: "0 -24px" }}>
        <FilterBar>
          <Input placeholder={t("questions.library.findAGroup", "Find a group…")} value={query} onChange={(e) => setQuery(e.target.value)} style={{ minWidth: 260 }}/>
          <span style={{ flex: 1 }}/><span style={{ fontSize: 12, color: T.soft }}>{groups.length} {t("questions.library.of", "of")}{" "}{allGroups.length}</span>
        </FilterBar>
        </div>
        <ExplainerFooter><b>{t("questions.library.groupsBundleQuestions", "Groups bundle questions")}</b> {t("questions.library.intoAnOrderedFormWithOptionalBranchingApplyAWholeGroupTo", "into an ordered form with optional branching. Apply a whole group to events to keep question sets consistent.")}</ExplainerFooter>

        {groups.length === 0
          ? <EmptyState variant="card" icon={<Icon.layers size={26}/>} title={t("questions.library.noGroupsMatch", "No groups match")} body={t("questions.library.createAGroupToBundleQuestions", "Create a group to bundle questions.")}
              action={canWrite ? <Btn icon={<Icon.plus size={14}/>} onClick={() => nav.go("questions.group.edit", { new: 1 })}>{t("questions.library.newGroup", "New group")}</Btn> : null}/>
          : view === "grid" ? (
            <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16, marginTop: 14 }}>
              {groups.map((g) => {
                const bc = branchCount(g);
                return (
                  <Card key={g.id} style={{ padding: 0, display: "flex", flexDirection: "column", cursor: "pointer", overflow: "hidden" }} onClick={() => nav.go("questions.group.edit", { id: g.id })}>
                    <div style={{ padding: "16px 18px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <span style={{ width: 32, height: 32, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.layers size={16}/></span>
                        <div><div style={{ fontSize: 14.5, fontWeight: 700, color: T.n1 }}>{g.name || "Untitled group"}</div>{g.description ? <div style={{ fontSize: 12, color: T.soft, marginTop: 3 }}>{g.description}</div> : null}</div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <Pill>{g.items.length} {t("questions.library.question1f5087db", "question")}{" "}{g.items.length === 1 ? "" : t('questions.library.text3')}</Pill>
                        {bc ? <Pill tone="primary">{bc} {t("questions.library.branch", "branch")}{" "}{bc === 1 ? "" : t('questions.library.text4')}</Pill> : null}
                      </div>
                      <div style={{ marginTop: "auto", paddingTop: 8, borderTop: `1px solid ${T.border}`, fontSize: 12, color: T.soft }}>{g.owner} · {g.updated}</div>
                    </div>
                    <CardActionBar {...mkActions(g)}/>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card style={{ padding: 0, marginTop: 14 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
                <thead><tr style={{ background: T.n6 }}><th style={qTh}>{t("questions.library.group", "Group")}</th><th style={qTh}>{t("questions.library.questions", "Questions")}</th><th style={qTh}>{t("questions.library.branches", "Branches")}</th><th style={qTh}>{t("questions.library.updated", "Updated")}</th><th style={{ ...qTh, width: 140 }}>{t("questions.library.actions", "Actions")}</th></tr></thead>
                <tbody>
                  {groups.map((g, i) => {
                    const bc = branchCount(g);
                    return (
                      <tr key={g.id} onClick={() => nav.go("questions.group.edit", { id: g.id })} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer" }}>
                        <td style={qTd}><div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ width: 30, height: 30, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.layers size={15}/></span>
                          <div><div style={{ color: T.n1, fontWeight: 600 }}>{g.name || "Untitled"}</div>{g.description ? <div style={{ color: T.soft, fontSize: 11.5 }}>{g.description}</div> : null}</div>
                        </div></td>
                        <td style={{ ...qTd, color: T.n1 }}>{g.items.length}</td>
                        <td style={{ ...qTd, color: bc ? T.p1 : T.soft }}>{bc || "—"}</td>
                        <td style={{ ...qTd, color: T.soft }}>{g.updated}</td>
                        <td style={qTd} onClick={(e) => e.stopPropagation()}><RowActions {...mkActions(g)}/></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
      </div>
    </Screen>
  );
}

export default function QuestionsLibrary() { return <QuestionsTab/>; }
export function QuestionGroupsLibrary() { return <GroupsTab/>; }
